// Sprint 11b.2 §2.3 — Wraps a WorkspaceAvatar with Radix ContextMenu.
//
// Right-click (desktop) opens the menu natively. Mobile devices have no
// right-click — we synthesize a `contextmenu` event at the touch position
// after a 600 ms hold (movement ≤ 5 px). Below 600 ms or with movement
// the dnd-kit TouchSensor (400 ms + 5 px tolerance, see WorkspaceColumn)
// takes over for drag-to-reorder; above 600 ms with no movement, this
// long-press handler wins.
//
// Menu items:
//   * "Edit project…" — opens EditProjectModal via `onEdit`.
//   * "Archive"       — calls `onArchive` (existing archive endpoint).

import { useEffect, useRef } from 'react';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from './ui/context-menu';
import { useTranslation } from '../i18n';

const LONG_PRESS_MS = 600;
const MOVEMENT_TOLERANCE_PX = 5;

export interface WorkspaceAvatarContextMenuProps {
  children: React.ReactNode;
  onEdit: () => void;
  onArchive: () => void;
  /** Disable the menu (e.g., for the default project that cannot be archived). */
  archiveDisabled?: boolean;
}

export function WorkspaceAvatarContextMenu({
  children,
  onEdit,
  onArchive,
  archiveDisabled,
}: WorkspaceAvatarContextMenuProps) {
  const { t } = useTranslation();
  const triggerRef = useRef<HTMLDivElement | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startPosRef = useRef<{ x: number; y: number } | null>(null);
  const firedRef = useRef(false);

  // Cleanup on unmount so a pending long-press timer doesn't fire after
  // the avatar is removed.
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  function clearTimer() {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }

  function onPointerDown(e: React.PointerEvent<HTMLDivElement>) {
    // Only act on touch / pen — mouse uses native right-click.
    if (e.pointerType === 'mouse') return;
    firedRef.current = false;
    startPosRef.current = { x: e.clientX, y: e.clientY };
    clearTimer();
    timerRef.current = setTimeout(() => {
      // Fire only if the pointer hasn't moved enough to trigger drag.
      // The dnd-kit TouchSensor activates at 400ms with movement > 5px;
      // we wait until 600ms to give it priority on movement-based gestures.
      if (firedRef.current) return;
      const target = triggerRef.current;
      if (!target) return;
      const ev = new MouseEvent('contextmenu', {
        bubbles: true,
        cancelable: true,
        clientX: startPosRef.current?.x ?? 0,
        clientY: startPosRef.current?.y ?? 0,
      });
      firedRef.current = true;
      target.dispatchEvent(ev);
    }, LONG_PRESS_MS);
  }

  function onPointerMove(e: React.PointerEvent<HTMLDivElement>) {
    if (!startPosRef.current) return;
    const dx = Math.abs(e.clientX - startPosRef.current.x);
    const dy = Math.abs(e.clientY - startPosRef.current.y);
    if (dx > MOVEMENT_TOLERANCE_PX || dy > MOVEMENT_TOLERANCE_PX) {
      clearTimer();
    }
  }

  function onPointerEnd() {
    clearTimer();
    startPosRef.current = null;
  }

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>
        <div
          ref={triggerRef}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerEnd}
          onPointerCancel={onPointerEnd}
          onPointerLeave={onPointerEnd}
          data-testid="workspace-avatar-context-trigger"
        >
          {children}
        </div>
      </ContextMenuTrigger>
      <ContextMenuContent>
        <ContextMenuItem onSelect={onEdit} data-testid="workspace-context-edit">
          {t('workspaceCustomization.edit')}
        </ContextMenuItem>
        <ContextMenuItem
          onSelect={onArchive}
          disabled={archiveDisabled}
          data-testid="workspace-context-archive"
        >
          {t('workspaceCustomization.archive')}
        </ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  );
}
